
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { UserProfile } from '../types';

import { api } from '../services/api';
import { authService } from '../services/authService';
import { US_STATES } from '../data/curriculumStandards';

// ...

const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'State' | 'National'>('State');
  const [loading, setLoading] = useState(false);

  // Load current user
  const [user, setUser] = useState<UserProfile | null>(null);

  const [leaderboardData, setLeaderboardData] = useState<any[]>([]);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const data = await api.get(`/leaderboard?scope=${activeTab}&state=${user.state}`);

        // Mark current user
        const processedData = data.map((item: any) => ({
          ...item,
          isCurrentUser: item.id === user.id
        }));

        setLeaderboardData(processedData);
      } catch (err) {
        console.error("Failed to fetch leaderboard", err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchLeaderboard();
    }
  }, [activeTab, user]);

  const getStateName = (code: string) => US_STATES.find(s => s.code === code)?.name || code;

  const getMedalColor = (rank: number) => {
    if (rank === 1) return 'text-yellow-400 drop-shadow-md';
    if (rank === 2) return 'text-slate-400 drop-shadow-md';
    if (rank === 3) return 'text-amber-700 drop-shadow-md';
    return 'text-slate-400 font-normal opacity-50';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
            <i className="fas fa-arrow-left"></i>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-indigo-900 flex items-center gap-2">
              <i className="fas fa-trophy text-brand-accent"></i> Leaderboard
            </h1>
            <p className="text-slate-500">See who's learning the most!</p>
          </div>
        </div>

        {/* User Mini Stat */}
        <div className="hidden sm:block bg-brand-blue text-white px-6 py-2 rounded-2xl shadow-lg">
          <p className="text-xs text-indigo-200 uppercase font-bold">Your Rank</p>
          <p className="text-2xl font-bold">#{leaderboardData.find(u => u.isCurrentUser)?.rank || '-'}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white p-1 rounded-2xl shadow-sm mb-6 border border-slate-100">
        {(['State', 'National'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === tab
              ? 'bg-brand-blue text-white shadow-md'
              : 'text-slate-500 hover:bg-slate-50'
              }`}
          >
            {tab === 'State' ? `${getStateName(user?.state || '')} State` : tab}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden min-h-[300px]">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-slate-400">
            <i className="fas fa-spinner fa-spin text-3xl mr-3"></i> Loading Leaderboard...
          </div>
        ) : (
          <>
            <div className="grid grid-cols-12 gap-4 p-4 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <div className="col-span-2 text-center">Rank</div>
              <div className="col-span-6 md:col-span-5">Student</div>
              <div className="col-span-2 hidden md:block">Grade</div>
              <div className="col-span-4 md:col-span-3 text-right pr-4">Points</div>
            </div>

            <div className="divide-y divide-slate-100">
              {leaderboardData.map((student) => (
                <div
                  key={student.id}
                  className={`grid grid-cols-12 gap-4 p-4 items-center transition-colors ${student.isCurrentUser
                    ? 'bg-indigo-50 border-l-4 border-brand-blue'
                    : 'hover:bg-slate-50'
                    }`}
                >
                  <div className="col-span-2 flex justify-center">
                    {student.rank <= 3 ? (
                      <div className={`text-2xl ${getMedalColor(student.rank)}`}>
                        <i className="fas fa-medal"></i>
                      </div>
                    ) : (
                      <span className="font-bold text-slate-400">#{student.rank}</span>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-xl border border-slate-200 shadow-sm">
                      {student.badge}
                    </div>
                    <div>
                      <p className={`font-bold ${student.isCurrentUser ? 'text-brand-blue' : 'text-slate-700'}`}>
                        {student.name}
                      </p>
                      <p className="text-xs text-slate-400 md:hidden">Grade {student.grade}</p>
                    </div>
                  </div>
                  <div className="col-span-2 hidden md:block text-slate-500 text-sm">
                    Grade {student.grade}
                  </div>
                  <div className="col-span-4 md:col-span-3 text-right pr-4">
                    <span className="font-bold text-brand-blue">{student.points.toLocaleString()}</span>
                    <span className="text-xs text-slate-400 ml-1">pts</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="mt-8 text-center text-slate-400 text-sm">
        <p><i className="fas fa-info-circle mr-1"></i> Leaderboards update every Sunday at midnight.</p>
      </div>
    </div>
  );
};

export default LeaderboardPage;
